import { Link } from 'react-router-dom';
import SectionTitle from '@/components/base/SectionTitle';
import type { Special } from '@/types/drama';

interface SpecialSpotlightProps {
  specials: Special[];
  dramaLookup: (id: string) => string;
}

export default function SpecialSpotlight({ specials, dramaLookup }: SpecialSpotlightProps) {
  const [main, ...rest] = specials;
  if (!main) return null;

  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
      <SectionTitle
        eyebrow="编辑策展"
        title="伦理剧专题精选"
        description="围绕角色、情感与主题的深度策展，帮助你系统性地理解韩国伦理剧的叙事魅力。"
        action={{ label: '查看全部专题', to: '/special' }}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 md:gap-5">
        <Link
          to="/special"
          className="group relative block cursor-pointer overflow-hidden rounded-lg border border-background-200"
        >
          <div className="relative h-64 w-full overflow-hidden bg-primary-900 md:h-full md:min-h-[360px]">
            <img
              src={main.cover}
              alt={`${main.title} 专题主图`}
              title={`${main.title} 韩国伦理剧专题`}
              className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/35 to-transparent" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
            <span className="inline-flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
              <i className="ri-bookmark-3-fill text-xs" />
              主推专题
            </span>
            <h3 className="mt-3 font-heading text-xl font-bold text-background-50 md:text-2xl">{main.title}</h3>
            <p className="mt-1 text-sm text-background-200">{main.subtitle}</p>
            <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-background-300">{main.intro}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {main.dramaIds.slice(0, 3).map((id) => (
                <span key={id} className="rounded-md bg-background-50/15 px-2 py-0.5 text-xs text-background-100">
                  {dramaLookup(id)}
                </span>
              ))}
            </div>
          </div>
        </Link>

        <div className="flex flex-col gap-4 md:gap-5">
          {rest.slice(0, 3).map((s, i) => (
            <Link
              key={s.id}
              to="/special"
              className="group flex cursor-pointer gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-accent-300 animate-fade-up md:p-4"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="relative h-24 w-32 shrink-0 overflow-hidden rounded-md bg-primary-900 sm:h-28 sm:w-40">
                <img
                  src={s.cover}
                  alt={`${s.title} 专题主图`}
                  title={`${s.title} 韩国伦理剧专题`}
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="min-w-0 flex-1 py-1">
                <h3 className="font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                  {s.title}
                </h3>
                <p className="mt-1 text-xs text-accent-600">{s.subtitle}</p>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{s.intro}</p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-600">
                  共 {s.dramaIds.length} 部作品
                  <i className="ri-arrow-right-s-line text-sm" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}